import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ChevronRight } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// Fluxo da Jornada — Candidato e Empresa
// Mostra as 4 fases do processo, destacando a fase atual
// ═══════════════════════════════════════════════════════════════

// ── Determina a fase atual do CANDIDATO com base nos dados ────
export function faseAtualCandidato({ pct = 0, onboarding = false, totalCurtidas = 0, foiSelecionado = false, foiAprovado = false }) {
  if (foiAprovado)                        return 4;
  if (foiSelecionado)                     return 3;
  if (totalCurtidas > 0)                  return 3; // curtiu pelo menos uma vaga
  if (onboarding || pct >= 50)            return 2;
  return 1;
}

// ── Determina a fase atual da EMPRESA com base nos dados ──────
export function faseAtualEmpresa({ temVagas = false, temMatches = false, temSelecao = false, temAprovados = false }) {
  if (temAprovados) return 4;
  if (temSelecao)   return 3;
  if (temMatches)   return 2;
  if (temVagas)     return 2; // tem vagas, aguardando candidatos
  return 1;
}

// ── Step individual ───────────────────────────────────────────
function Step({ numero, icon, titulo, descricao, acao, to, status, isLast }) {
  // status: 'concluido' | 'ativo' | 'pendente'
  const estilos = {
    concluido: {
      circulo:  'bg-acre-green text-white shadow-sm shadow-acre-green/30',
      card:     'bg-acre-light/60 border-acre-green/30',
      titulo:   'text-acre-green',
      descricao:'text-gray-500',
      linha:    'bg-acre-green',
    },
    ativo: {
      circulo:  'bg-gradient-to-br from-acre-green to-acre-teal text-white shadow-md shadow-acre-green/40 ring-4 ring-acre-green/20',
      card:     'bg-gradient-to-br from-acre-light to-white border-acre-green shadow-sm',
      titulo:   'text-acre-dark',
      descricao:'text-gray-600',
      linha:    'bg-gray-200',
    },
    pendente: {
      circulo:  'bg-gray-100 text-gray-400',
      card:     'bg-gray-50 border-gray-100',
      titulo:   'text-gray-400',
      descricao:'text-gray-400',
      linha:    'bg-gray-200',
    },
  };

  const s = estilos[status];

  return (
    <div className="flex items-start gap-0 flex-col sm:flex-row sm:flex-1">
      {/* Conteúdo do step */}
      <div className="flex flex-row sm:flex-col items-start sm:items-center w-full">
        {/* Círculo + linha horizontal (desktop) */}
        <div className="flex sm:flex-col items-center w-full sm:w-auto">
          {/* Número / check */}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all ${s.circulo}`}>
            {status === 'concluido'
              ? <CheckCircle size={18}/>
              : <span>{numero}</span>}
          </div>

          {/* Linha vertical (mobile) */}
          {!isLast && (
            <div className={`w-0.5 h-6 sm:hidden mt-1 mb-1 ml-5 ${s.linha}`}/>
          )}
        </div>

        {/* Card */}
        <div className={`sm:mt-3 ml-4 sm:ml-0 flex-1 sm:w-full border rounded-2xl p-3.5 transition-all ${s.card}`}>
          <div className="flex items-start gap-2.5 sm:flex-col sm:items-center sm:text-center">
            <span className="text-2xl flex-shrink-0">{icon}</span>
            <div className="sm:text-center">
              <p className={`font-sora font-bold text-sm leading-tight ${s.titulo}`}>{titulo}</p>
              <p className={`text-xs mt-0.5 leading-relaxed ${s.descricao}`}>{descricao}</p>
              {status === 'ativo' && acao && to && (
                <Link to={to}
                  className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-white bg-acre-green hover:bg-acre-mid px-2.5 py-1.5 rounded-lg transition-colors">
                  {acao} <ChevronRight size={11}/>
                </Link>
              )}
              {status === 'concluido' && (
                <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-acre-green font-semibold">
                  <CheckCircle size={11}/> Concluído
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Linha horizontal entre steps (desktop) */}
      {!isLast && (
        <div className={`hidden sm:flex items-center justify-center w-8 flex-shrink-0 mt-5`}>
          <div className={`h-0.5 w-full ${s.linha}`}/>
        </div>
      )}
    </div>
  );
}

// ── Fluxo do CANDIDATO ────────────────────────────────────────
export function FluxoCandidato({ faseAtual = 1 }) {
  const passos = [
    {
      icon: '🎯',
      titulo: 'Crie seu Perfil',
      descricao: 'A IA Mikhal monta seu currículo com Hard Skills, Soft Skills e Fit Cultural.',
      acao: 'Completar perfil',
      to: '/candidato/perfil',
    },
    {
      icon: '💚',
      titulo: 'Encontre Vagas',
      descricao: 'Veja vagas compatíveis com seu perfil e dê like nas que te interessam.',
      acao: 'Ver vagas para mim',
      to: '/candidato/matches',
    },
    {
      icon: '📋',
      titulo: 'Em Seleção',
      descricao: 'Uma empresa escolheu você! Aguarde o contato pelo chat do sistema.',
      acao: 'Ver mensagens',
      to: '/chat',
    },
    {
      icon: '🤝',
      titulo: 'Contratado!',
      descricao: 'Entrevista, aprovação e seu novo emprego começa. Parabéns!',
      acao: null,
      to: null,
    },
  ];

  return (
    <div className="card p-5 mb-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">🗺️</span>
        <div>
          <p className="font-sora font-bold text-acre-dark text-sm">Sua jornada no AcreJob</p>
          <p className="text-xs text-gray-400">Fase {faseAtual} de 4 — {
            faseAtual === 1 ? 'Complete seu perfil para começar'
          : faseAtual === 2 ? 'Explore as vagas compatíveis'
          : faseAtual === 3 ? 'Você está no processo seletivo!'
          : 'Jornada concluída 🎉'
          }</p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {[1,2,3,4].map(i => (
            <div key={i} className={`w-6 h-1.5 rounded-full transition-colors ${
              i < faseAtual ? 'bg-acre-green' :
              i === faseAtual ? 'bg-acre-gold' :
              'bg-gray-200'
            }`}/>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-1 sm:gap-0">
        {passos.map((p, i) => {
          const num = i + 1;
          const status = num < faseAtual ? 'concluido' : num === faseAtual ? 'ativo' : 'pendente';
          return (
            <Step
              key={i}
              numero={num}
              icon={p.icon}
              titulo={p.titulo}
              descricao={p.descricao}
              acao={p.acao}
              to={p.to}
              status={status}
              isLast={i === passos.length - 1}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Fluxo da EMPRESA ──────────────────────────────────────────
export function FluxoEmpresa({ faseAtual = 1 }) {
  const passos = [
    {
      icon: '📋',
      titulo: 'Publique a Vaga',
      descricao: 'Descreva a oportunidade, requisitos e benefícios.',
      acao: 'Publicar vaga',
      to: '/empresa/vagas/nova',
    },
    {
      icon: '🎯',
      titulo: 'Veja os Matches',
      descricao: 'A IA rankeia candidatos por % de compatibilidade automaticamente.',
      acao: 'Ver candidatos',
      to: '/empresa/vagas',
    },
    {
      icon: '💬',
      titulo: 'Selecione & Converse',
      descricao: 'Escolha candidatos, converse pelo chat e agende entrevistas.',
      acao: 'Em seleção',
      to: '/empresa/selecao',
    },
    {
      icon: '✅',
      titulo: 'Contrate!',
      descricao: 'Aprove o melhor candidato e feche a vaga com sucesso.',
      acao: null,
      to: null,
    },
  ];

  return (
    <div className="card p-5 mb-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">🗺️</span>
        <div>
          <p className="font-sora font-bold text-acre-dark text-sm">Fluxo de contratação</p>
          <p className="text-xs text-gray-400">Fase {faseAtual} de 4 — {
            faseAtual === 1 ? 'Publique sua primeira vaga'
          : faseAtual === 2 ? 'Candidatos compatíveis aguardam'
          : faseAtual === 3 ? 'Candidatos em processo seletivo'
          : 'Contratação concluída 🎉'
          }</p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {[1,2,3,4].map(i => (
            <div key={i} className={`w-6 h-1.5 rounded-full transition-colors ${
              i < faseAtual ? 'bg-acre-green' :
              i === faseAtual ? 'bg-acre-gold' :
              'bg-gray-200'
            }`}/>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-1 sm:gap-0">
        {passos.map((p, i) => {
          const num = i + 1;
          const status = num < faseAtual ? 'concluido' : num === faseAtual ? 'ativo' : 'pendente';
          return (
            <Step
              key={i}
              numero={num}
              icon={p.icon}
              titulo={p.titulo}
              descricao={p.descricao}
              acao={p.acao}
              to={p.to}
              status={status}
              isLast={i === passos.length - 1}
            />
          );
        })}
      </div>
    </div>
  );
}
